import { VisualShape } from 'app/frontend/types/custom/VisualPlan'

const LAYOUT_INSET = 0.6
const GRID_INSET = 0.9
const FILL_MIN_COUNT = 5
const RING_COUNT_BIAS = 1.3

export type ClosedShape = Extract<
  VisualShape,
  { type: 'rect' | 'circle' | 'triangle' }
>

export const isClosedShape = (shape: VisualShape): shape is ClosedShape =>
  shape.type === 'rect' || shape.type === 'circle' || shape.type === 'triangle'

export type EntityLayout = {
  centerX: number
  centerY: number
  rotation: number
  rx: number
  ry: number
  positions: { x: number; y: number }[]
}

type LayoutMode = 'radial' | 'grid' | 'hex' | 'triangle'

export const getShapeHalfExtents = (
  shape: ClosedShape,
  width: number,
  height: number,
): { rx: number; ry: number } => {
  if (shape.type === 'circle') {
    return {
      rx: (shape.radius_x ?? shape.radius ?? 0.08) * width,
      ry: (shape.radius_y ?? shape.radius ?? 0.08) * width,
    }
  }

  return {
    rx: (shape.width * width) / 2,
    ry: (shape.height * height) / 2,
  }
}

const radialPositions = (
  count: number,
  rx: number,
  ry: number,
): { x: number; y: number }[] => {
  if (count <= 0) return []
  if (count === 1) return [{ x: 0, y: 0 }]

  const raw = Array.from({ length: count }, (_, i) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / count
    return { x: Math.cos(angle), y: Math.sin(angle) }
  })

  const xs = raw.map((p) => p.x)
  const ys = raw.map((p) => p.y)
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)
  const boxWidth = maxX - minX || 1
  const boxHeight = maxY - minY || 1
  const boxCx = (minX + maxX) / 2
  const boxCy = (minY + maxY) / 2

  const layoutRx = rx * LAYOUT_INSET
  const layoutRy = ry * LAYOUT_INSET
  const scaleX = (2 * layoutRx) / boxWidth
  const scaleY = (2 * layoutRy) / boxHeight

  return raw.map((p) => ({
    x: (p.x - boxCx) * scaleX,
    y: (p.y - boxCy) * scaleY,
  }))
}

const gridPositions = (
  count: number,
  rx: number,
  ry: number,
): { x: number; y: number }[] => {
  if (count <= 0) return []
  if (count === 1) return [{ x: 0, y: 0 }]

  const aspect = rx / Math.max(ry, 1e-6)
  let cols = Math.max(1, Math.ceil(Math.sqrt(count * aspect)))
  const rows = Math.max(1, Math.ceil(count / cols))
  while (cols > 1 && (cols - 1) * rows >= count) cols -= 1

  const layoutRx = rx * GRID_INSET
  const layoutRy = ry * GRID_INSET
  const cellW = (2 * layoutRx) / cols
  const cellH = (2 * layoutRy) / rows
  const positions: { x: number; y: number }[] = []
  let placed = 0

  for (let row = 0; row < rows; row++) {
    const remaining = count - placed
    const rowsLeft = rows - row
    const colsInRow = Math.ceil(remaining / rowsLeft)
    const rowWidth = colsInRow * cellW
    const startX = -rowWidth / 2 + cellW / 2

    for (let col = 0; col < colsInRow; col++) {
      positions.push({
        x: startX + col * cellW,
        y: -layoutRy + (row + 0.5) * cellH,
      })
      placed += 1
    }
  }

  return positions
}

const allocateRings = (total: number, rings: number): number[] | null => {
  if (total < rings) return null

  const weights = Array.from({ length: rings }, (_, i) => i + 1)
  const totalWeight = weights.reduce((sum, w) => sum + w, 0)
  const raw = weights.map((w) => (total * w) / totalWeight)
  const sizes = raw.map((value) => Math.max(1, Math.floor(value)))

  let sum = sizes.reduce((acc, size) => acc + size, 0)
  while (sum < total) {
    let best = 0
    for (let i = 1; i < rings; i++) {
      if (raw[i] - sizes[i] > raw[best] - sizes[best]) best = i
    }
    sizes[best] += 1
    sum += 1
  }
  while (sum > total) {
    let best = -1
    for (let i = 0; i < rings; i++) {
      if (
        sizes[i] > 1 &&
        (best === -1 || sizes[i] - raw[i] > sizes[best] - raw[best])
      ) {
        best = i
      }
    }
    if (best === -1) break
    sizes[best] -= 1
    sum -= 1
  }

  return sizes
}

const buildRingPositions = (
  ringSizes: number[],
): { x: number; y: number }[] => {
  const rings = ringSizes.length
  const positions: { x: number; y: number }[] = [{ x: 0, y: 0 }]

  ringSizes.forEach((size, index) => {
    const ringIndex = index + 1
    const radiusT = ringIndex / rings
    const angleOffset = ringIndex % 2 === 1 ? 0 : Math.PI / size

    for (let i = 0; i < size; i++) {
      const angle = -Math.PI / 2 + angleOffset + (2 * Math.PI * i) / size
      positions.push({
        x: radiusT * Math.cos(angle),
        y: radiusT * Math.sin(angle),
      })
    }
  })

  return positions
}

const minPairDistance = (positions: { x: number; y: number }[]): number => {
  let min = Infinity
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const dx = positions[i].x - positions[j].x
      const dy = positions[i].y - positions[j].y
      min = Math.min(min, Math.hypot(dx, dy))
    }
  }
  return min
}

const balancedRingPositions = (
  count: number,
  rx: number,
  ry: number,
): { x: number; y: number }[] => {
  if (count <= 0) return []
  if (count === 1) return [{ x: 0, y: 0 }]

  const maxRings = Math.max(1, Math.ceil(Math.sqrt(count)))
  let best: { positions: { x: number; y: number }[]; score: number } | null =
    null

  const singleRing = Array.from({ length: count }, (_, i) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / count
    return { x: Math.cos(angle), y: Math.sin(angle) }
  })
  best = { positions: singleRing, score: minPairDistance(singleRing) }

  for (let rings = 1; rings <= maxRings; rings++) {
    const sizes = allocateRings(count - 1, rings)
    if (!sizes) continue
    const positions = buildRingPositions(sizes)
    const score =
      minPairDistance(positions) * Math.pow(RING_COUNT_BIAS, rings - 1)
    if (score > best.score) best = { positions, score }
  }

  const layoutRx = rx * GRID_INSET
  const layoutRy = ry * GRID_INSET
  const chosen = best?.positions ?? [{ x: 0, y: 0 }]

  return chosen.map((p) => ({ x: p.x * layoutRx, y: p.y * layoutRy }))
}

const buildTriangleRows = (
  rowSizes: number[],
  layoutRx: number,
  layoutRy: number,
): { x: number; y: number }[] => {
  const rows = rowSizes.length
  const positions: { x: number; y: number }[] = []

  rowSizes.forEach((cols, row) => {
    const t = (row + 0.5) / rows
    const rowHalfW = layoutRx * t
    const y = -layoutRy + t * 2 * layoutRy

    for (let col = 0; col < cols; col++) {
      positions.push({
        x: cols === 1 ? 0 : -rowHalfW + ((col + 0.5) / cols) * 2 * rowHalfW,
        y,
      })
    }
  })

  return positions
}

const triangleRowPositions = (
  count: number,
  rx: number,
  ry: number,
): { x: number; y: number }[] => {
  if (count <= 0) return []
  if (count === 1) return [{ x: 0, y: 0 }]

  const layoutRx = rx * GRID_INSET
  const layoutRy = ry * GRID_INSET
  const maxRows = Math.max(1, Math.ceil(Math.sqrt(2 * count)) + 1)
  let best: { positions: { x: number; y: number }[]; spread: number } | null =
    null

  for (let rows = 1; rows <= maxRows; rows++) {
    const sizes = allocateRings(count, rows)
    if (!sizes) continue
    const positions = buildTriangleRows(sizes, layoutRx, layoutRy)
    const spread = minPairDistance(positions)
    if (!best || spread > best.spread) best = { positions, spread }
  }

  return best?.positions ?? [{ x: 0, y: 0 }]
}

export const entityPositions = (
  count: number,
  rx: number,
  ry: number,
  mode: LayoutMode = 'radial',
): { x: number; y: number }[] => {
  if (mode === 'grid') return gridPositions(count, rx, ry)
  if (mode === 'hex') return balancedRingPositions(count, rx, ry)
  if (mode === 'triangle') return triangleRowPositions(count, rx, ry)
  return radialPositions(count, rx, ry)
}

const layoutModeFor = (shape: ClosedShape, count: number): LayoutMode => {
  if (shape.type === 'circle') return 'hex'
  if (shape.type === 'triangle') return 'triangle'
  if (count < FILL_MIN_COUNT) return 'radial'
  return 'grid'
}

export const getEntityLayout = (
  shape: ClosedShape,
  width: number,
  height: number,
  count: number,
): EntityLayout => {
  const { rx, ry } = getShapeHalfExtents(shape, width, height)
  return {
    centerX: shape.x * width,
    centerY: shape.y * height,
    rotation: shape.rotation ?? 0,
    rx,
    ry,
    positions: entityPositions(count, rx, ry, layoutModeFor(shape, count)),
  }
}
